import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdCheck, MdClose, MdArrowBack, MdAutorenew } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import Button from 'components/Button';
import Loader from 'components/Loader';

import './index.css';

type QuizResult = {
    questionId: number;
    word: string;
    definition: string;
    sentence: string;
    selectedWord: string | null;
    correctWord: string;
    isCorrect: boolean;
    choices: string[];
};

type QuizData = {
    quizId: number;
    vocabSetId: number;
    attempt: number;
    testNumber: number;
    score: number;
    correct: number;
    total: number;
    completedDate?: string;
    results: QuizResult[];
};

type Filter = 'all' | 'incorrect' | 'correct';

function VocabQuizResult() {
    const [data, setData] = useState<QuizData | null>(null);
    const [filter, setFilter] = useState<Filter>('all');

    const { quizId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { organization } = useAppSelector(state => state.organization);

    const fromHistory = (location.state as any)?.from === 'history';

    useEffect(() => {
        axios.get(`vocab/quiz/${quizId}/`)
            .then(res => setData(res.data))
            .catch(() => navigate('/vocab'));
    }, [quizId]);

    if (!data) return <Loader />;

    const backPath = fromHistory ? '/vocab' : (data.vocabSetId ? `/vocab/flashcards/${data.vocabSetId}` : '/vocab');
    const backLabel = fromHistory ? 'Back to Quiz History' : 'Back to Flashcards';

    const correctResults = data.results.filter(r => r.isCorrect);
    const wrongResults = data.results.filter(r => !r.isCorrect);
    const displayResults = filter === 'all'
        ? data.results
        : filter === 'incorrect'
            ? wrongResults
            : correctResults;

    const pct = data.score;
    const circumference = 2 * Math.PI * 54;
    const strokeOffset = circumference - (pct / 100) * circumference;
    const scoreColor = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';

    return (
        <div className='qr__page'>
            <Helmet>
                <title>Quiz Results - {organization.name}</title>
            </Helmet>

            {/* Header */}
            <div className='qr__header'>
                <button className='qr__backBtn' onClick={() => navigate(backPath, fromHistory ? { state: { tab: 1 } } : {})}>
                    <MdArrowBack />
                    <span>{backLabel}</span>
                </button>
                <span className='qr__headerBadge'>Quiz {data.testNumber} Results</span>
            </div>

            <div className='qr'>
                {/* Left sidebar */}
                <div className='qr__sidebar'>
                    <div className='qr__scoreCard'>
                        {/* SVG ring */}
                        <div className='qr__ring'>
                            <svg viewBox='0 0 120 120' className='qr__ringSvg'>
                                <circle cx='60' cy='60' r='54' className='qr__ringBg' />
                                <circle
                                    cx='60' cy='60' r='54'
                                    className='qr__ringFill'
                                    style={{
                                        strokeDasharray: circumference,
                                        strokeDashoffset: strokeOffset,
                                        stroke: scoreColor,
                                    }}
                                />
                            </svg>
                            <div className='qr__ringLabel'>
                                <span className='qr__ringPct'>{Math.round(pct)}%</span>
                            </div>
                        </div>

                        <div className='qr__scoreDetails'>
                            <div className='qr__scoreStat'>
                                <span className='qr__scoreStatNum' style={{ color: '#10b981' }}>{data.correct}</span>
                                <span className='qr__scoreStatLabel'>Correct</span>
                            </div>
                            <div className='qr__scoreDivider' />
                            <div className='qr__scoreStat'>
                                <span className='qr__scoreStatNum' style={{ color: '#ef4444' }}>{data.total - data.correct}</span>
                                <span className='qr__scoreStatLabel'>Missed</span>
                            </div>
                            <div className='qr__scoreDivider' />
                            <div className='qr__scoreStat'>
                                <span className='qr__scoreStatNum'>{data.total}</span>
                                <span className='qr__scoreStatLabel'>Total</span>
                            </div>
                        </div>
                    </div>

                    {wrongResults.length > 0 && (
                        <div className='qr__notice'>
                            <MdAutorenew className='qr__noticeIcon' />
                            <p>{wrongResults.length} missed {wrongResults.length === 1 ? 'word' : 'words'} moved to your learning pile</p>
                        </div>
                    )}

                    <div className='qr__actions'>
                        <Button onClick={() => navigate(backPath, fromHistory ? { state: { tab: 1 } } : {})} fullWidth>
                            {backLabel}
                        </Button>
                        {wrongResults.length > 0 && (
                            <Button
                                onClick={() => navigate(`/vocab/flashcards/${data.vocabSetId}`)}
                                secondary
                                fullWidth
                            >
                                Study Missed Words
                            </Button>
                        )}
                    </div>
                </div>

                {/* Right content */}
                <div className='qr__main'>
                    <div className='qr__filters'>
                        <button
                            className={`qr__filter ${filter === 'all' ? 'qr__filter--active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            All <span className='qr__filterCount'>{data.total}</span>
                        </button>
                        <button
                            className={`qr__filter ${filter === 'correct' ? 'qr__filter--active qr__filter--green' : ''}`}
                            onClick={() => setFilter('correct')}
                        >
                            Correct <span className='qr__filterCount'>{correctResults.length}</span>
                        </button>
                        <button
                            className={`qr__filter ${filter === 'incorrect' ? 'qr__filter--active qr__filter--red' : ''}`}
                            onClick={() => setFilter('incorrect')}
                        >
                            Incorrect <span className='qr__filterCount'>{wrongResults.length}</span>
                        </button>
                    </div>

                    <div className='qr__list'>
                        {displayResults.map((result, idx) => (
                            <div
                                key={result.questionId}
                                className={`qr__item ${result.isCorrect ? 'qr__item--correct' : 'qr__item--wrong'}`}
                            >
                                <div className='qr__itemStatus'>
                                    <div className={`qr__itemBadge ${result.isCorrect ? 'qr__itemBadge--correct' : 'qr__itemBadge--wrong'}`}>
                                        {result.isCorrect ? <MdCheck /> : <MdClose />}
                                    </div>
                                </div>
                                <div className='qr__itemBody'>
                                    <p className='qr__itemSentence'>{result.sentence}</p>
                                    <div className='qr__itemMeta'>
                                        <span className='qr__itemWord'>{result.correctWord}</span>
                                        <span className='qr__itemDef'>{result.definition}</span>
                                    </div>
                                    {!result.isCorrect && (
                                        <div className='qr__itemYours'>
                                            {result.selectedWord
                                                ? <>You chose: <span className='qr__itemStrike'>{result.selectedWord}</span></>
                                                : 'Unanswered'
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VocabQuizResult;
